import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card";
import { Button } from "@/project/karuna/components/ui/button";
import { ShieldOff } from "lucide-react";
import type { AdminPermissionKey } from "@/project/karuna/constants/adminPermissions";
import { useAdminPermissions } from "@/project/karuna/contexts/AdminPermissionsContext";
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import { AdminLayout } from "@/project/karuna/pages/admin/AdminLayout";

interface RequireAdminPermissionProps {
  permission: AdminPermissionKey;
  children: ReactNode;
}

export function RequireAdminPermission({
  permission,
  children,
}: RequireAdminPermissionProps) {
  const { hasPermission, hasAnyPermission } = useAdminPermissions();
  const getLink = useKarunaLink();

  if (hasPermission(permission)) {
    return children;
  }

  if (hasAnyPermission()) {
    return <Navigate to={getLink("/admin/dashboard")} replace />;
  }

  return (
    <AdminLayout>
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <ShieldOff className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You do not have permission to access this section. Contact your
              master administrator to request access.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button variant="outline" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
